import { useEffect, useRef, useState } from 'react';
import { 
  initPerformanceMonitoring, 
  trackMemoryUsage, 
  monitorNetworkConnection,
  getPerformanceSummary
} from '../utils/performance';

interface PerformanceMetrics {
  cls?: number;
  fid?: number;
  fcp?: number;
  lcp?: number;
  ttfb?: number;
  memory?: {
    usedJSHeapSize: number;
    totalJSHeapSize: number;
    jsHeapSizeLimit: number;
  } | null;
  isOnline: boolean;
  performanceSummary?: ReturnType<typeof getPerformanceSummary>;
}

export const usePerformanceMonitoring = () => {
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    isOnline: navigator.onLine,
    performanceSummary: getPerformanceSummary()
  });
  
  const metricsRef = useRef<PerformanceMetrics>({
    isOnline: navigator.onLine,
    performanceSummary: getPerformanceSummary()
  });
  
  // Initialize performance monitoring
  useEffect(() => {
    initPerformanceMonitoring((metric) => {
      metricsRef.current = {
        ...metricsRef.current,
        [metric.name.toLowerCase()]: metric.value
      };
      
      setMetrics(prevMetrics => ({
        ...prevMetrics,
        [metric.name.toLowerCase()]: metric.value
      }));
      
      // Log to console for debugging
      console.log(`[Web Vital] ${metric.name}: ${metric.value}`);
    });
    
    // Set up memory usage tracking
    const memoryIntervalId = setInterval(() => {
      const memoryMetrics = trackMemoryUsage();
      if (memoryMetrics) {
        metricsRef.current.memory = memoryMetrics;
        setMetrics(prevMetrics => ({
          ...prevMetrics,
          memory: memoryMetrics
        }));
      }
    }, 10000); // Check every 10 seconds
    
    // Update performance summary periodically
    const summaryIntervalId = setInterval(() => {
      const summary = getPerformanceSummary();
      metricsRef.current.performanceSummary = summary;
      setMetrics(prevMetrics => ({
        ...prevMetrics,
        performanceSummary: summary
      }));
    }, 15000); // Update every 15 seconds
    
    // Set up network connection monitoring
    const cleanupNetworkMonitoring = monitorNetworkConnection((online) => {
      metricsRef.current.isOnline = online;
      setMetrics(prevMetrics => ({
        ...prevMetrics,
        isOnline: online
      }));
    });
    
    return () => {
      clearInterval(memoryIntervalId);
      clearInterval(summaryIntervalId);
      cleanupNetworkMonitoring();
    };
  }, []);
  
  return {
    metrics,
    getCurrentMetrics: () => metricsRef.current,
    getPerformanceSummary
  };
};